import React from 'react';
import { theme } from '@garnish/constants';

import { LoadingAnimation } from '@order/components';
import type { GetLoyaltyProfileQuery } from '@order/graphql';
import { TierName } from '@order/graphql';
import { useSweetpass } from '@order/hooks';

import { SweetpassConfirmationDialog } from '../../components/SweetpassConfirmationDialog';
import type { SweetpassUpgradeScreenProps } from '../../navigation/AppNavigation.props';
import { getSweetpassUpgradeScreenPaletteForTag } from './SweetgreenUpgradeScreenPalette';
import type {
  SweetpassUpgradeCtaHandler,
  SweetpassUpgradePageTag,
} from './SweetpassUpgradeScreen.loaded';
import { useUpgradeScreenLoadedContent } from './SweetpassUpgradeScreen.loaded';
import { SweetpassUpgradeScreenContent } from './SweetpassUpgradeScreenContent';

// ────────────────────────────────────────────────────────────────────────────────

export const SweetpassUpgradeScreen = ({
  navigation,
}: SweetpassUpgradeScreenProps) => {
  const {
    isSweetpassEnabled,
    isSweetpassFlagLoading,
    loyaltyProfile,
    isLoyaltyProfileLoading,
  } = useSweetpass();

  const [showConfirmationModal, setShowConfirmationModal] =
    React.useState(false);

  const upgradePageTag = getUpgradePageTagForLoyaltyProfile(loyaltyProfile);

  const palette = getSweetpassUpgradeScreenPaletteForTag(upgradePageTag);

  const { content: loadedContent, isLoading: isContentLoading } =
    useUpgradeScreenLoadedContent(
      isLoyaltyProfileLoading ? undefined : upgradePageTag ?? '',
    );

  const isLoading =
    isSweetpassFlagLoading || isLoyaltyProfileLoading || isContentLoading;

  const hasContent =
    isSweetpassEnabled && Boolean(loadedContent) && Boolean(palette);

  const shouldRedirectToHome = !isLoading && !hasContent;

  // ─────────────────────────────────────────────────────────────────

  React.useLayoutEffect(() => {
    if (shouldRedirectToHome) {
      navigation.navigate('MainTabs', {
        screen: 'HomeTab',
        params: { screen: 'Home' },
      });

      return;
    }

    navigation.setOptions({
      headerStyle: {
        backgroundColor:
          palette?.header.backgroundColor ?? theme.colors.KHAKI_3,
      },
      headerTintColor: palette?.header.titleColor,
    });
  }, [navigation, palette, shouldRedirectToHome]);

  const handleCtaConfirmation: SweetpassUpgradeCtaHandler = React.useCallback(
    (action) => {
      // eslint-disable-next-line no-console
      console.log('ACTION:', action);
    },
    [],
  );

  const handleCta: SweetpassUpgradeCtaHandler = React.useCallback(
    (action) => {
      if (action === 'join_sweetpass') {
        setShowConfirmationModal(true);
      } else {
        handleCtaConfirmation(action);
      }
    },
    [handleCtaConfirmation],
  );

  const handleDismissModal = React.useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  // ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return <LoadingAnimation />;
  }

  if (!isSweetpassEnabled || !loadedContent || !palette) {
    return null;
  }

  return (
    <>
      <SweetpassUpgradeScreenContent
        testID="sweetpass-upgrade-screen"
        content={loadedContent}
        palette={palette}
        ctaHandler={handleCta}
      />
      <SweetpassConfirmationDialog
        visible={showConfirmationModal}
        onDismiss={handleDismissModal}
        onConfirm={() => {
          handleCtaConfirmation(loadedContent.cta.action);
        }}
      />
    </>
  );
};

const getUpgradePageTagForLoyaltyProfile = (
  loyaltyProfile?: GetLoyaltyProfileQuery['loyaltyProfile'],
): SweetpassUpgradePageTag | undefined => {
  const nextTierName = loyaltyProfile?.tier?.nextTier?.name;

  if (nextTierName === TierName.SweetpassPlus) {
    return 'upgrade-to-sweetpass-plus';
  }

  if (nextTierName === TierName.Sweetpass) {
    return 'join-sweetpass';
  }

  return undefined;
};
